/**
 * @fileoverview Job Notice of Appointment Settings Panel
 *
 * Purpose: Renders the Notice of Appointment tab inside the Job Settings
 * page. Shows file metadata, an inline preview, and lets owner/member
 * users replace the uploaded file with a fresh one (which is then re-run
 * through the standard processing pipeline).
 *
 * Used by:
 * - src/routes/_app/_auth/dashboard/_layout.jobs.$jobId.settings.tsx
 */

import { useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexMutation,
  useConvexAction,
} from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { JobRole } from "@cvx/jobs/schema";
import { Button } from "@/ui/primitives/button";
import { Card } from "@/ui/primitives/card";
import {
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

interface Props {
  jobId: Id<"jobs">;
  userRole: JobRole;
}

export function JobNoticeOfAppointmentPanel({ jobId, userRole }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data: noa, isLoading } = useQuery(
    convexQuery(api.jobs.noticeOfAppointment.queries.getNoticeOfAppointment, {
      jobId,
    }),
  );

  const generateUploadUrl = useConvexMutation(
    api.jobs.noticeOfAppointment.mutations.generateNoaUploadUrl,
  );
  const { mutateAsync: replaceNoa } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.noticeOfAppointment.mutations.replaceNoticeOfAppointment,
    ),
  });
  const { mutateAsync: updateJob } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });
  // MOR-429: run NOA extraction after Replace so day-one fields populate
  // automatically. Same action used by the create-job wizard.
  const { mutateAsync: extractNoaFields } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.noticeOfAppointment.actions.extractNoaFields,
    ),
  });

  const canEdit = userRole === "owner" || userRole === "member";
  const [isReExtracting, setIsReExtracting] = useState(false);

  const handleReExtract = async () => {
    if (!noa) return;
    if (!noa.storageId) {
      toast.error(
        "Unable to re-extract — please refresh the page and try again.",
      );
      return;
    }
    setIsReExtracting(true);
    try {
      const extracted = await extractNoaFields({
        storageId: noa.storageId,
        fileName: noa.fileName,
        fileType: noa.fileType,
        fileSize: noa.fileSize,
        jobId,
      });
      const jobPatch: Parameters<typeof updateJob>[0] = { jobId };
      if (extracted.appointmentType)
        jobPatch.appointmentType = extracted.appointmentType;
      if (extracted.appointmentDate)
        jobPatch.appointmentDate = extracted.appointmentDate;
      if (extracted.notesBlock) jobPatch.notes = extracted.notesBlock;
      if (extracted.courtName) jobPatch.courtName = extracted.courtName;
      if (extracted.courtState) jobPatch.courtState = extracted.courtState;
      if (extracted.courtNumber) jobPatch.courtNumber = extracted.courtNumber;
      if (extracted.sequestrationNumber)
        jobPatch.sequestrationNumber = extracted.sequestrationNumber;
      if (extracted.proceedingMatterNumber)
        jobPatch.proceedingMatterNumber = extracted.proceedingMatterNumber;
      if (extracted.proceedingYear)
        jobPatch.proceedingYear = extracted.proceedingYear;
      if (extracted.courtOrderDate)
        jobPatch.courtOrderDate = extracted.courtOrderDate;
      if (extracted.windingUpApplicationDate)
        jobPatch.windingUpApplicationDate = extracted.windingUpApplicationDate;
      if (extracted.administrationStartDate)
        jobPatch.administrationStartDate = extracted.administrationStartDate;
      if (extracted.plaintiffName)
        jobPatch.plaintiffName = extracted.plaintiffName;
      if (extracted.defendantName)
        jobPatch.defendantName = extracted.defendantName;
      if (extracted.bankruptName)
        jobPatch.bankruptName = extracted.bankruptName;
      if (extracted.administrationNumber)
        jobPatch.administrationNumber = extracted.administrationNumber;
      if (extracted.petitioningCreditorCosts)
        jobPatch.petitioningCreditorCosts = extracted.petitioningCreditorCosts;
      if (extracted.actOfBankruptcyDate)
        jobPatch.actOfBankruptcyDate = extracted.actOfBankruptcyDate;
      if (Object.keys(jobPatch).length > 1) {
        await updateJob(jobPatch);
        toast.success(
          "Appointment details refreshed from the Notice of Appointment",
        );
      } else {
        toast.info(
          "No additional details could be extracted from this document",
        );
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      toast.error(`Extraction failed: ${msg}`);
    } finally {
      setIsReExtracting(false);
    }
  };

  const handleFileSelected = async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error("Please upload a PDF or Word document.");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error("File is too large. Maximum size is 25 MB.");
      return;
    }

    setIsUploading(true);
    try {
      const uploadUrl = await generateUploadUrl({});
      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }
      const { storageId } = (await uploadResponse.json()) as {
        storageId: Id<"_storage">;
      };

      await replaceNoa({
        jobId,
        storageId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
      });

      // MOR-429: re-run extraction against the new file and persist
      // every NOA-derived field it surfaces. Failure here is non-fatal
      // — the replacement file is already saved and downstream
      // processing will run regardless, so the user gets a softer
      // warning rather than a hard error if Sonnet times out.
      //
      // Mirrors the create-job wizard: appointment metadata
      // (type, date, notes block) plus every day-one court/bankruptcy
      // field. Company-identity fields (entityName, ACN, ABN, address,
      // name variants) are intentionally NOT patched here — those are
      // set during job creation, are stable across the life of the
      // job, and overwriting them silently on a NOA replace would
      // surprise the user.
      const didExtractFields = await (async (): Promise<boolean> => {
        try {
          const extracted = await extractNoaFields({
            storageId,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            jobId,
          });
          const jobPatch: Parameters<typeof updateJob>[0] = { jobId };
          if (extracted.appointmentType) {
            jobPatch.appointmentType = extracted.appointmentType;
          }
          if (extracted.appointmentDate) {
            jobPatch.appointmentDate = extracted.appointmentDate;
          }
          if (extracted.notesBlock) jobPatch.notes = extracted.notesBlock;
          if (extracted.courtName) jobPatch.courtName = extracted.courtName;
          if (extracted.courtState) jobPatch.courtState = extracted.courtState;
          if (extracted.courtNumber)
            jobPatch.courtNumber = extracted.courtNumber;
          if (extracted.sequestrationNumber) {
            jobPatch.sequestrationNumber = extracted.sequestrationNumber;
          }
          if (extracted.proceedingMatterNumber) {
            jobPatch.proceedingMatterNumber = extracted.proceedingMatterNumber;
          }
          if (extracted.proceedingYear) {
            jobPatch.proceedingYear = extracted.proceedingYear;
          }
          if (extracted.courtOrderDate) {
            jobPatch.courtOrderDate = extracted.courtOrderDate;
          }
          if (extracted.windingUpApplicationDate) {
            jobPatch.windingUpApplicationDate =
              extracted.windingUpApplicationDate;
          }
          if (extracted.administrationStartDate) {
            jobPatch.administrationStartDate =
              extracted.administrationStartDate;
          }
          if (extracted.plaintiffName) {
            jobPatch.plaintiffName = extracted.plaintiffName;
          }
          if (extracted.defendantName) {
            jobPatch.defendantName = extracted.defendantName;
          }
          if (extracted.bankruptName) {
            jobPatch.bankruptName = extracted.bankruptName;
          }
          if (extracted.administrationNumber) {
            jobPatch.administrationNumber = extracted.administrationNumber;
          }
          if (extracted.petitioningCreditorCosts) {
            jobPatch.petitioningCreditorCosts =
              extracted.petitioningCreditorCosts;
          }
          if (extracted.actOfBankruptcyDate) {
            jobPatch.actOfBankruptcyDate = extracted.actOfBankruptcyDate;
          }
          if (Object.keys(jobPatch).length > 1) {
            await updateJob(jobPatch);
            return true;
          }
          return false;
        } catch (extractErr) {
          const msg =
            extractErr instanceof Error
              ? extractErr.message
              : String(extractErr);
          toast.warning(
            `Notice of Appointment uploaded, but field extraction failed: ${msg}`,
          );
          return false;
        }
      })();

      toast.success(
        didExtractFields
          ? "Notice of Appointment updated — appointment details refreshed from the new file"
          : "Notice of Appointment updated",
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to upload file";
      toast.error(message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {noa ? (
        <div className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold tracking-tight">
            Instrument of Appointment
          </h2>
          <NoaCard
            fileName={noa.fileName}
            fileType={noa.fileType}
            fileSize={noa.fileSize}
            uploadedAt={noa.uploadedAt}
            downloadUrl={noa.downloadUrl}
            extractionStatus={noa.extractionStatus}
            classificationStatus={noa.classificationStatus}
            canEdit={canEdit}
            isUploading={isUploading}
            isReExtracting={isReExtracting}
            onReplaceClick={() => fileInputRef.current?.click()}
            onReExtractClick={handleReExtract}
          />
        </div>
      ) : (
        <EmptyState
          canEdit={canEdit}
          isUploading={isUploading}
          onUploadClick={() => fileInputRef.current?.click()}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFileSelected(file);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface NoaCardProps {
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: number;
  downloadUrl: string | null;
  extractionStatus?: string;
  classificationStatus?: string;
  canEdit: boolean;
  isUploading: boolean;
  isReExtracting: boolean;
  onReplaceClick: () => void;
  onReExtractClick: () => void;
}

function NoaCard({
  fileName,
  fileType,
  fileSize,
  uploadedAt,
  downloadUrl,
  extractionStatus,
  classificationStatus,
  canEdit,
  isUploading,
  isReExtracting,
  onReplaceClick,
  onReExtractClick,
}: NoaCardProps) {
  const isPdf = fileType === "application/pdf";
  const processing =
    extractionStatus === "pending" ||
    extractionStatus === "extracting" ||
    classificationStatus === "pending" ||
    classificationStatus === "classifying";

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{fileName}</p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(fileSize)} · Uploaded {formatDate(uploadedAt)}
            </p>
            {processing && (
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                Processing through document pipeline…
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {downloadUrl && (
            <Button asChild variant="outline" size="sm" className="gap-2">
              <a href={downloadUrl} download={fileName}>
                <Download className="h-3.5 w-3.5" />
                Download
              </a>
            </Button>
          )}
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={onReExtractClick}
              disabled={isReExtracting || isUploading}
            >
              {isReExtracting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Re-extract
            </Button>
          )}
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={onReplaceClick}
              disabled={isUploading || isReExtracting}
            >
              {isUploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Replace
            </Button>
          )}
        </div>
      </div>

      {isPdf && downloadUrl ? (
        <iframe
          src={downloadUrl}
          title={fileName}
          className="min-h-[900px] w-full border-0"
        />
      ) : (
        <div className="flex h-32 items-center justify-center bg-muted/30 text-sm text-muted-foreground">
          Inline preview is only available for PDFs. Use Download to open the
          file.
        </div>
      )}
    </Card>
  );
}

function EmptyState({
  canEdit,
  isUploading,
  onUploadClick,
}: {
  canEdit: boolean;
  isUploading: boolean;
  onUploadClick: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold tracking-tight">
          Instrument of Appointment
        </h2>
        <p className="text-sm text-muted-foreground">
          Upload the Instrument of Appointment to auto-populate appointment
          details above.
        </p>
      </header>
      <Card className="flex flex-col items-center justify-center gap-3 border-dashed p-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <FileText className="h-6 w-6" />
        </div>
        <div>
          <p className="text-sm font-medium">
            No Instrument of Appointment uploaded
          </p>
          <p className="text-xs text-muted-foreground">
            Upload the signed Instrument of Appointment to keep it on file and
            reuse it as an attachment for precedents.
          </p>
        </div>
        {canEdit && (
          <Button
            type="button"
            variant="default"
            size="sm"
            className="mt-2 gap-2"
            onClick={onUploadClick}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            Upload Instrument of Appointment
          </Button>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
